import React, { useState } from "react";
import { useContext } from "react";
import { Alert, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router";
import { Woo } from "../../../../Helpers/Woo";
import { Context } from "../../../../Shared/Context/GlobalContext";
import { useInputValue } from "../../../../Shared/Hooks/useInputValue";
import routes from "../../../../Helpers/Routes.json";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Trans, useTranslation } from "react-i18next";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

export const CreateCustomerFormComponent = ({ isRegister }) => {
  const { setUser, domain } = useContext(Context);
  const email = useInputValue("", "email");
  const repeatEmail = useInputValue("", "repeat-email");
  const password = useInputValue("", "password");
  const phone = useInputValue("", "email");
  const name = useInputValue("", "name");
  const surname = useInputValue("", "surname");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const shopName = domain?.shop?.name || "";
  const privacyUrl = domain?.shop?.privacy_url || "#";
  const termsUrl = domain?.shop?.terms_url || "#";

  function validateFields(fields) {
    const { email, repeatEmail } = fields;
    const validationErrors = [];

    if (!email || !email.includes("@")) {
      validationErrors.push(t("El email no es válido"));
    }

    if (email !== repeatEmail) {
      validationErrors.push(t("Los emails no coinciden"));
    }

    if (!fields.phone || !isValidPhoneNumber(fields.phone)) {
      validationErrors.push("No parece un teléfono válido.");
    }

    return validationErrors;
  }

  function createCustomerPost(e) {
    e.preventDefault();

    const validationErrors = validateFields({
      email: email.value,
      repeatEmail: repeatEmail.value,
      phone: phone.value,
    });

    if (validationErrors.length) {
      setLoading(false);
      setError(validationErrors.join("\n"));
      return;
    }

    const postData = {
      email: email.value,
      username: email.value,
      password: password.value,
      phone: phone.value,
      value: true,
      billing: {
        first_name: name.value,
        last_name: surname.value,
        hola: true,
      },
    };

    setLoading(true);
    Woo.customers
      .post(postData)
      .then(async (res) => {
        try {
          const data = await res.json();
          if (data.error) {
            setLoading(false);
            setError(data.error);
          } else {
            setUser(data.data);
          }
        } catch (e) {}
      })
      .catch((e) => {
        setError("Algo ha ido mal, por favor inténtalo de nuevo más tarde.");
      });
  }

  return (
    <div className="card p-4">
      <h1 className="h3 fw-bold mb-4">{t("Regístrate")}</h1>
      {error && <Alert variant="danger" className="small">{error}</Alert>}
      <Form onSubmit={createCustomerPost}>
        <div className="row g-2 mb-3">
          <div className="col">
            <Form.Label>Nombre</Form.Label>
            <Form.Control required {...name} autoComplete="given-name" />
          </div>
          <div className="col">
            <Form.Label>Apellidos</Form.Label>
            <Form.Control {...surname} autoComplete="family-name" />
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Label>{t("Email")}</Form.Label>
          <Form.Control type="email" {...email} required autoComplete="email" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t("Confirma Email")}</Form.Label>
          <Form.Control
            type="email"
            {...repeatEmail}
            required
            autoComplete="email"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t("Teléfono")}</Form.Label>
          <PhoneInput
            defaultCountry="ES"
            flagUrl="https://purecatamphetamine.github.io/country-flag-icons/3x2/{XX}.svg"
            onChange={phone.setValue}
            value={phone.value}
            className="w-100"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t("Contraseña")}</Form.Label>
          <InputGroup>
            <Form.Control
              required
              {...password}
              type={showPassword ? "text" : "password"}
              minLength={6}
              autoComplete="new-password"
            />
            <InputGroup.Text
              role="button"
              className="bg-white"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </InputGroup.Text>
          </InputGroup>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Check
            required
            id="terms-check"
            name="terms"
            label={
              <Trans
                i18nKey={
                  "Estoy de acuerdo con la <PrivacyLink>Política de privacidad</PrivacyLink> y los <TermsLink>Términos y condiciones</TermsLink> de {{shopName}}."
                }
                values={{ shopName }}
                components={{
                  PrivacyLink: (
                    <a
                      href={privacyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-decoration-underline"
                    />
                  ),
                  TermsLink: (
                    <a
                      href={termsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-decoration-underline"
                    />
                  ),
                }}
              />
            }
          />
        </Form.Group>
        <Button
          type="submit"
          variant="dark"
          className="w-100 rounded-pill d-flex align-items-center justify-content-center gap-2"
          disabled={loading || email.value !== repeatEmail.value}
        >
          {loading && <Spinner animation="border" size="sm" />}
          {t("Registrarse")}
        </Button>
      </Form>
      <hr />
      <div className="text-center small">
        <span>{t("Ya tengo una cuenta")} </span>
        <span
          role="button"
          className="fw-bold"
          style={{ color: "var(--tws-brown)", cursor: "pointer" }}
          onClick={() => {
            if (isRegister) {
              isRegister(false);
            } else {
              navigate(routes.LoginPage.index);
            }
          }}
        >
          {t("Iniciar sesión")}
        </span>
      </div>
    </div>
  );
};
